/**
 * ChartRenderer — Premium chart visualizations from AI responses
 *
 * The AI can include chart data in fenced code blocks:
 *
 *   ```chart
 *   {
 *     "type": "bar",
 *     "title": "2025年每月进口票数",
 *     "xKey": "month",
 *     "yKey": "count",
 *     "data": [
 *       { "month": "1月", "count": 233 },
 *       { "month": "2月", "count": 175 }
 *     ]
 *   }
 *   ```
 *
 * Supported chart types: bar, line, pie, area
 * Features: gradient fills, smooth animations, glass-morphism tooltip, auto-detect keys
 */

import { useMemo } from 'react';
import {
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ─── Types ────────────────────────────────────────

export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'area';
  title?: string;
  xKey: string;
  yKey: string | string[];
  data: Record<string, unknown>[];
  colors?: string[];
}

/**
 * Flexible chart block detection — handles AI format variations.
 * Accepts data with explicit xKey/yKey OR auto-detects from data structure.
 * Common AI patterns: { data: [{ label, value }], options: {...} }
 */
export function isChartBlock(obj: unknown): obj is ChartData {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  if (typeof o.type !== 'string' || !['bar', 'line', 'pie', 'area'].includes(o.type)) return false;
  if (!Array.isArray(o.data) || o.data.length === 0) return false;
  // Accept if xKey is provided OR if we can auto-detect from first data item
  if (typeof o.xKey === 'string') return true;
  // Auto-detect: first string key = xKey, first number key = yKey
  const first = o.data[0] as Record<string, unknown>;
  if (first && typeof first === 'object') {
    const hasString = Object.values(first).some(v => typeof v === 'string');
    const hasNumber = Object.values(first).some(v => typeof v === 'number');
    return hasString && hasNumber;
  }
  return false;
}

/** Normalize AI chart data — auto-detect xKey/yKey if missing */
export function normalizeChartData(raw: Record<string, unknown>): ChartData {
  const data = raw as unknown as ChartData;
  if (data.xKey && data.yKey) return data;

  // Auto-detect from first data item
  const first = (data.data[0] ?? {}) as Record<string, unknown>;
  const keys = Object.keys(first);
  const stringKey = keys.find(k => typeof first[k] === 'string') ?? keys[0];
  const numberKeys = keys.filter(k => typeof first[k] === 'number');

  return {
    ...data,
    xKey: data.xKey ?? stringKey ?? 'label',
    yKey: data.yKey ?? (numberKeys.length === 1 ? numberKeys[0] : numberKeys) ?? 'value',
  };
}

// ─── Premium Color Palette ────────────────────────

const CHART_COLORS = [
  ['#818cf8', '#4f46e5'], // indigo
  ['#34d399', '#059669'], // emerald
  ['#fbbf24', '#d97706'], // amber
  ['#f87171', '#dc2626'], // red
  ['#22d3ee', '#0891b2'], // cyan
  ['#f472b6', '#db2777'], // pink
  ['#a78bfa', '#7c3aed'], // violet
  ['#2dd4bf', '#0d9488'], // teal
] as const;

const PIE_COLORS = [
  '#818cf8', '#34d399', '#fbbf24', '#f87171',
  '#22d3ee', '#f472b6', '#a78bfa', '#2dd4bf',
  '#fb923c', '#a3e635', '#c084fc', '#38bdf8',
];

/** Generate gradient defs for SVG charts */
function GradientDefs({ count }: { count: number }) {
  return (
    <defs>
      {Array.from({ length: count }, (_, i) => {
        const [light, dark] = CHART_COLORS[i % CHART_COLORS.length]!;
        return (
          <linearGradient key={`grad-${i}`} id={`chartGrad${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={light} stopOpacity={0.9} />
            <stop offset="100%" stopColor={dark} stopOpacity={0.7} />
          </linearGradient>
        );
      })}
      {/* Area fill gradients (semi-transparent) */}
      {Array.from({ length: count }, (_, i) => {
        const [light] = CHART_COLORS[i % CHART_COLORS.length]!;
        return (
          <linearGradient key={`areaGrad-${i}`} id={`areaGrad${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={light} stopOpacity={0.3} />
            <stop offset="100%" stopColor={light} stopOpacity={0.02} />
          </linearGradient>
        );
      })}
    </defs>
  );
}

// ─── Glass-morphism Tooltip ───────────────────────

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-white/20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl px-4 py-3 shadow-xl shadow-black/10 text-xs ring-1 ring-black/5">
      <p className="font-semibold text-foreground mb-1.5 text-[13px]">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: entry.color }} />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-bold text-foreground ml-auto tabular-nums">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Custom pie chart label with clean formatting */
function PieLabel({ cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, name = '', value = 0, percent = 0 }: {
  cx?: number; cy?: number; midAngle?: number; innerRadius?: number; outerRadius?: number;
  name?: string; value?: number; percent?: number;
}) {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.04) return null; // hide tiny slices
  return (
    <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central"
      className="fill-foreground text-[11px] font-medium">
      {name} ({value.toLocaleString()})
    </text>
  );
}

// ─── Shared axis / grid styling ───────────────────

const axisStyle = { fontSize: 11, fill: 'var(--color-muted-foreground, #94a3b8)' };
const gridProps = { strokeDasharray: '3 6', strokeOpacity: 0.15, vertical: false } as const;
const ANIM_DURATION = 800;
const ANIM_EASING = 'ease-out' as const;

// ─── Chart Component ──────────────────────────────

export function ChartRenderer({ data }: { data: ChartData }) {
  const yKeys = useMemo(() => {
    if (Array.isArray(data.yKey)) return data.yKey;
    return data.yKey ? [data.yKey] : ['value'];
  }, [data.yKey]);

  const userColors = data.colors;

  return (
    <div className="my-4 rounded-xl border bg-gradient-to-b from-background to-muted/30 p-5 shadow-sm">
      {data.title && (
        <h4 className="text-sm font-semibold mb-4 text-center tracking-tight">{data.title}</h4>
      )}
      <ResponsiveContainer width="100%" height={340}>
        {data.type === 'bar' ? (
          <BarChart data={data.data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }} barCategoryGap="20%">
            <GradientDefs count={yKeys.length} />
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={data.xKey} tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={50}
              tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v)} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--color-muted)', opacity: 0.3, radius: 4 }} />
            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />}
            {yKeys.map((key, i) => (
              <Bar key={key} dataKey={key}
                fill={userColors ? userColors[i % userColors.length] : `url(#chartGrad${i})`}
                radius={[6, 6, 0, 0]}
                animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING}
                maxBarSize={56} />
            ))}
          </BarChart>
        ) : data.type === 'line' ? (
          <LineChart data={data.data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <GradientDefs count={yKeys.length} />
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={data.xKey} tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={50}
              tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v)} />
            <Tooltip content={<CustomTooltip />} />
            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />}
            {yKeys.map((key, i) => {
              const [light, dark] = CHART_COLORS[i % CHART_COLORS.length]!;
              const color = userColors ? userColors[i % userColors.length] : dark;
              return (
                <Line key={key} type="monotone" dataKey={key}
                  stroke={color} strokeWidth={2.5}
                  dot={{ r: 4, fill: '#fff', stroke: color, strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: light, stroke: '#fff', strokeWidth: 2 }}
                  animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING} />
              );
            })}
          </LineChart>
        ) : data.type === 'area' ? (
          <AreaChart data={data.data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <GradientDefs count={yKeys.length} />
            <CartesianGrid {...gridProps} />
            <XAxis dataKey={data.xKey} tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={50}
              tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v)} />
            <Tooltip content={<CustomTooltip />} />
            {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />}
            {yKeys.map((key, i) => {
              const [, dark] = CHART_COLORS[i % CHART_COLORS.length]!;
              const color = userColors ? userColors[i % userColors.length] : dark;
              return (
                <Area key={key} type="monotone" dataKey={key}
                  stroke={color} strokeWidth={2.5}
                  fill={userColors ? color : `url(#areaGrad${i})`}
                  fillOpacity={userColors ? 0.15 : 1}
                  dot={{ r: 3, fill: '#fff', stroke: color, strokeWidth: 2 }}
                  activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
                  animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING} />
              );
            })}
          </AreaChart>
        ) : data.type === 'pie' ? (
          <PieChart>
            <Pie
              data={data.data}
              dataKey={yKeys[0]}
              nameKey={data.xKey}
              cx="50%" cy="50%"
              innerRadius="40%" outerRadius="70%"
              paddingAngle={3}
              cornerRadius={4}
              label={PieLabel}
              labelLine={{ strokeWidth: 1, stroke: 'var(--color-muted-foreground, #94a3b8)' }}
              animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING}
            >
              {data.data.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}
                  stroke="var(--color-background, #fff)" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(value: string) => <span className="text-foreground text-xs">{value}</span>} />
          </PieChart>
        ) : (
          <BarChart data={data.data}>
            <GradientDefs count={1} />
            <Bar dataKey={yKeys[0]} fill="url(#chartGrad0)" radius={[6, 6, 0, 0]}
              animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
